// Locale-aware copy for the city guides' "Weather by month" block: the table's
// rows and the FAQ entries generated from the same normals (src/lib/climate.ts),
// so the visible answers, the FAQPage JSON-LD and the table can't disagree.
// Month names are read off the resource JSON directly (arrays — no
// `returnObjects` dance through t()) and are authored rather than taken from
// Intl, since the FAQ needs the declined "в октябре" form Intl doesn't give.

import { CLIMATE_FAQ_MONTHS, SEA_TEMPS, cityClimate, maxMonth, minMonth } from '@/lib/climate';
import type { CityGuideFaqEntry } from '@/lib/i18n/city-content';
import { cityInPhrase } from '@/lib/i18n/city-listings-content';
import type { Locale } from '@todo/core/i18n/locales';
import en from '@/lib/i18n/resources/en.json';
import fr from '@/lib/i18n/resources/fr.json';
import ru from '@/lib/i18n/resources/ru.json';

type Translate = (key: string, options?: Record<string, unknown>) => string;

type ClimateResource = { cityClimate: { months: string[]; inMonth: string[] } };

const resources: Record<Locale, ClimateResource> = { en, fr, ru };

export type ClimateRow = {
  month: string;
  high: number;
  low: number;
  sea: number;
  rainDays: number;
};

export type CityClimateCopy = {
  heading: string;
  columns: { month: string; high: string; low: string; sea: string; rain: string };
  rows: ClimateRow[];
  note: string;
  source: string;
};

/** The table's copy and rounded rows, or undefined for a city without normals. */
export function cityClimateCopy(cityId: string, locale: Locale, t: Translate): CityClimateCopy | undefined {
  const climate = cityClimate(cityId);
  const inCity = cityInPhrase(locale, cityId);
  if (!climate || !inCity) return undefined;
  const { months } = resources[locale].cityClimate;

  return {
    heading: t('cityClimate.heading', { inCity }),
    columns: {
      month: t('cityClimate.columns.month'),
      high: t('cityClimate.columns.high'),
      low: t('cityClimate.columns.low'),
      sea: t('cityClimate.columns.sea'),
      rain: t('cityClimate.columns.rain'),
    },
    rows: climate.months.map((month, index) => ({
      month: months[index],
      high: Math.round(month.high),
      low: Math.round(month.low),
      sea: Math.round(SEA_TEMPS[index]),
      rainDays: Math.round(month.rainDays),
    })),
    note: t('cityClimate.note'),
    source: t('cityClimate.source', {
      period: climate.period,
      station: t(`cityClimate.stations.${cityId}`),
    }),
  };
}

/**
 * The weather FAQ entries appended to the city guide's hand-written FAQ: the
 * year at a glance, the shoulder months by name, and when the sea is warm
 * enough to swim. Built from the city's own normals so every city's answers
 * differ, and rounded the same way as the table.
 */
export function cityClimateFaq(cityId: string, locale: Locale, t: Translate): CityGuideFaqEntry[] {
  const climate = cityClimate(cityId);
  const inCity = cityInPhrase(locale, cityId);
  if (!climate || !inCity) return [];
  const { inMonth } = resources[locale].cityClimate;
  const round = Math.round;

  const cold = minMonth(climate, (m) => m.high);
  const hot = maxMonth(climate, (m) => m.high);
  const wet = maxMonth(climate, (m) => m.rainDays);
  const dry = minMonth(climate, (m) => m.rainDays);
  const lows = climate.months.map((m) => m.low);

  const faq: CityGuideFaqEntry[] = [
    {
      q: t('cityClimate.faq.byMonthQ', { inCity }),
      a: t('cityClimate.faq.byMonthA', {
        coldHigh: round(climate.months[cold].high),
        coldMonth: inMonth[cold],
        hotHigh: round(climate.months[hot].high),
        hotMonth: inMonth[hot],
        coldLow: round(Math.min(...lows)),
        hotLow: round(Math.max(...lows)),
        wetMonth: inMonth[wet],
        wetDays: round(climate.months[wet].rainDays),
        dryMonth: inMonth[dry],
      }),
    },
  ];

  for (const index of CLIMATE_FAQ_MONTHS) {
    const month = climate.months[index];
    faq.push({
      q: t('cityClimate.faq.monthQ', { inCity, month: inMonth[index] }),
      a: t('cityClimate.faq.monthA', {
        Month: capitalize(inMonth[index]),
        high: round(month.high),
        low: round(month.low),
        sea: round(SEA_TEMPS[index]),
        rain: round(month.rainDays),
      }),
    });
  }

  faq.push({ q: t('cityClimate.faq.swimQ', { inCity }), a: t('cityClimate.faq.swimA') });
  return faq;
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}
